import { get } from 'lodash-es';
import * as React from 'react';
import {
  useFormState,
  type Control,
  type FieldError,
  type FieldErrors,
} from 'react-hook-form';

// 集合演算 util
const set = {
  // A - B
  difference: <T,>(a: Set<T>, b: Set<T>) =>
    new Set([...a].filter((x) => !b.has(x))),
  // A ∩ B
  intersection: <T,>(a: Set<T>, b: Set<T>) =>
    new Set([...a].filter((x) => b.has(x))),
};

type TouchedFields = {
  [key: string]: boolean | TouchedFields;
};

const touchedFieldsToPaths = (
  touchedFields: TouchedFields,
  prefix = '',
): string[] => {
  return Object.entries(touchedFields).reduce((acc, [key, value]) => {
    if (typeof value === 'boolean') {
      return value ? [...acc, prefix + key] : acc;
    }
    return [...acc, ...touchedFieldsToPaths(value, prefix + key + '.')];
  }, []);
};
const getErrors = (errors: FieldErrors, touchedFields: string[]) => {
  return new Map(
    touchedFields
      .map((v) => [v, get(errors, v)] as const)
      .filter((v): v is [(typeof v)[0], FieldError] => v[1] !== undefined),
  );
};
const diffErrors = (a: Map<string, FieldError>, b: Map<string, FieldError>) => {
  // a と b の key を比較して、 a にあって b にないものを取得する
  // ただし、両方に共通したとしても、 error の内容が異なる場合はそれも取得する
  const diffKeys = set.difference(new Set(a.keys()), new Set(b.keys()));
  const sharedKeys = set.intersection(new Set(a.keys()), new Set(b.keys()));

  const errors = new Map<string, FieldError>();
  diffKeys.forEach((k) => errors.set(k, a.get(k)!));
  sharedKeys.forEach((k) => {
    const aError = a.get(k)!;
    const bError = b.get(k)!;
    if (aError.type !== bError.type || aError.message !== bError.message) {
      errors.set(k, aError);
    }
  });

  return errors;
};

export type ValidateFillItemResult =
  | {
      success: true;
    }
  | {
      success: false;
      error: FieldError;
    };

export type FormEventHandlers = {
  onValidateFillItem?: (key: string, value: ValidateFillItemResult) => void;
  onFieldFocusIn?: (key: string) => void;
  onFieldFocusOut?: (key: string) => void;
};

export type UseFormEventOptions = {
  ref: React.RefObject<HTMLFormElement>;
  control: Control;
  handlers?: FormEventHandlers;
};

export const useFormEvent = ({
  ref,
  control,
  handlers,
}: UseFormEventOptions) => {
  const { errors, touchedFields } = useFormState({ control });
  const lastTouchedFieldPaths = React.useRef(
    new Set(touchedFieldsToPaths(touchedFields)),
  );
  const lastErrors = React.useRef(
    getErrors(errors, Array.from(lastTouchedFieldPaths.current)),
  );
  const debounce = React.useRef(new Map<string, number>());

  const currentTouchedFieldPaths = new Set(touchedFieldsToPaths(touchedFields));
  const currentErrors = getErrors(errors, Array.from(currentTouchedFieldPaths));

  const runDebounce = (key: string, value: ValidateFillItemResult) => {
    const timeoutId = debounce.current.get(key);
    const DEBOUNCE_TIME = 5000;
    const handler = () => {
      debounce.current.delete(key);
      handlers?.onValidateFillItem?.(key, value);
    };
    if (!timeoutId) {
      debounce.current.set(key, window.setTimeout(handler, DEBOUNCE_TIME));
    } else {
      // lastCalled がある場合、元の debounce を削除して新しい debounce をセットする
      window.clearTimeout(timeoutId);
      debounce.current.set(key, window.setTimeout(handler, DEBOUNCE_TIME));
    }
  };

  // touchedFields と prevTouchedFields の差分を取得し、
  // 新たに touched された field を確認する
  const addedTouchedFieldPaths = set.difference(
    currentTouchedFieldPaths,
    lastTouchedFieldPaths.current,
  );
  lastTouchedFieldPaths.current = currentTouchedFieldPaths;
  // error の差分を取得し、新たに error が追加された field を確認する
  const addedErrors = diffErrors(currentErrors, lastErrors.current);
  const removedErrorFields = set.difference(
    new Set(lastErrors.current.keys()),
    new Set(currentErrors.keys()),
  );
  lastErrors.current = currentErrors;

  if (addedTouchedFieldPaths.size > 0) {
    // console.log(`touchedFields added:`, Array.from(addedTouchedFieldPaths))
    addedTouchedFieldPaths.forEach((v) => {
      lastTouchedFieldPaths.current.add(v);
      // 今はまだ error の内容が取得できなかったりするので、少し後にエラーを取得するように遅延させる
      const error = get(errors, v) as FieldError | undefined;
      // console.log(`error<${v}>`, error)
      if (error) {
        // lastFieldError と同様の場合はスキップ
        runDebounce(v, { success: false, error });
      } else {
        // 入力成功
        runDebounce(v, { success: true });
      }
    });
  }
  if (addedErrors.size > 0) {
    // console.log(`errors added or changed:`, Array.from(addedErrors.keys()))
    addedErrors.forEach((error, key) => {
      runDebounce(key, { success: false, error });
      // console.log(`入力失敗<${key}>`, error)
    });
  }
  if (removedErrorFields.size > 0) {
    // console.log(`errors removed:`, Array.from(removedErrorFields))
    removedErrorFields.forEach((v) => {
      runDebounce(v, { success: true });
      // console.log(`入力成功<${v}>`)
    });
  }

  React.useEffect(() => {
    const abort = new AbortController();
    const getName = (element: EventTarget | null) => {
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
      ) {
        return element.name;
      }
      return null;
    };
    ref.current?.addEventListener(
      'focusin',
      (e) => {
        const name = getName(e.target);
        if (name) {
          handlers?.onFieldFocusIn?.(name);
        }
      },
      { signal: abort.signal },
    );
    ref.current?.addEventListener(
      'focusout',
      (e) => {
        const name = getName(e.target);
        if (name) {
          handlers?.onFieldFocusOut?.(name);
        }
      },
      { signal: abort.signal },
    );
    return () => {
      abort.abort();
    };
  }, [handlers?.onFieldFocusIn, handlers?.onFieldFocusOut, ref]);
};
